import type { RegionEffect } from "./types";

/**
 * A rasterised selection. `data` is RGBA as returned by getImageData and
 * only its alpha channel is read; (x, y) place it inside the image.
 */
export interface Mask {
  data: Uint8ClampedArray;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Bounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Alpha at which an antialiased edge pixel counts as selected. Kept low so a
 * shape's soft edge is covered rather than leaking the original pixels.
 */
const COVERAGE_THRESHOLD = 26;

const BLUR_PASSES = 3;

export function cellCounts(
  width: number,
  height: number,
  blockSize: number,
): [number, number] {
  return [
    Math.max(1, Math.ceil(width / blockSize)),
    Math.max(1, Math.ceil(height / blockSize)),
  ];
}

/** Cell boundaries that spread the remainder evenly, like the upstream tool. */
export function cellEdges(length: number, cells: number): number[] {
  return Array.from({ length: cells + 1 }, (_, index) =>
    index === 0 ? 0 : Math.ceil((index * length) / cells - 0.5),
  );
}

export function blurRadiusForBlockSize(blockSize: number): number {
  return Math.max(2, Math.round(blockSize * 0.6));
}

function isCovered(mask: Mask, x: number, y: number): boolean {
  return mask.data[((y - mask.y) * mask.width + (x - mask.x)) * 4 + 3] >= COVERAGE_THRESHOLD;
}

/** Tight bounds of the selected pixels in image coordinates. */
export function maskBounds(mask: Mask): Bounds | null {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (let row = 0; row < mask.height; row += 1) {
    for (let column = 0; column < mask.width; column += 1) {
      if (mask.data[(row * mask.width + column) * 4 + 3] < COVERAGE_THRESHOLD) continue;
      left = Math.min(left, column);
      right = Math.max(right, column);
      top = Math.min(top, row);
      bottom = Math.max(bottom, row);
    }
  }
  if (right < 0) return null;
  return {
    left: mask.x + left,
    top: mask.y + top,
    right: mask.x + right + 1,
    bottom: mask.y + bottom + 1,
  };
}

function writePixel(
  target: Uint8ClampedArray,
  offset: number,
  red: number,
  green: number,
  blue: number,
  alpha: number,
): void {
  target[offset] = red;
  target[offset + 1] = green;
  target[offset + 2] = blue;
  target[offset + 3] = alpha;
}

/**
 * Cells sit on one image-wide grid, so neighbouring ranges with the same size
 * line up. Colours average the whole cell from the source, but only selected
 * pixels are written, which keeps thin strokes and curved edges intact.
 */
function applyMosaic(
  source: ImageData,
  target: Uint8ClampedArray,
  mask: Mask,
  bounds: Bounds,
  blockSize: number,
): void {
  const { width, height, data } = source;
  const [cellsWide, cellsHigh] = cellCounts(width, height, blockSize);
  const xEdges = cellEdges(width, cellsWide);
  const yEdges = cellEdges(height, cellsHigh);

  for (let cellY = 0; cellY < cellsHigh; cellY += 1) {
    const top = yEdges[cellY];
    const bottom = yEdges[cellY + 1];
    if (bottom <= bounds.top || top >= bounds.bottom) continue;
    for (let cellX = 0; cellX < cellsWide; cellX += 1) {
      const left = xEdges[cellX];
      const right = xEdges[cellX + 1];
      if (right <= bounds.left || left >= bounds.right) continue;

      let red = 0;
      let green = 0;
      let blue = 0;
      let alpha = 0;
      for (let y = top; y < bottom; y += 1) {
        for (let x = left; x < right; x += 1) {
          const offset = (y * width + x) * 4;
          red += data[offset];
          green += data[offset + 1];
          blue += data[offset + 2];
          alpha += data[offset + 3];
        }
      }
      const count = (right - left) * (bottom - top);
      if (count === 0) continue;
      red = Math.round(red / count);
      green = Math.round(green / count);
      blue = Math.round(blue / count);
      alpha = Math.round(alpha / count);

      for (let y = Math.max(top, bounds.top); y < Math.min(bottom, bounds.bottom); y += 1) {
        for (let x = Math.max(left, bounds.left); x < Math.min(right, bounds.right); x += 1) {
          if (!isCovered(mask, x, y)) continue;
          writePixel(target, (y * width + x) * 4, red, green, blue, alpha);
        }
      }
    }
  }
}

/** One separable box-blur pass; edges clamp so borders do not wrap. */
function blurAxis(
  source: Uint8ClampedArray,
  target: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number,
  horizontal: boolean,
): void {
  const lineLength = horizontal ? width : height;
  const lineCount = horizontal ? height : width;
  const step = (horizontal ? 1 : width) * 4;
  const lineStep = (horizontal ? width : 1) * 4;
  const window = radius * 2 + 1;
  const clamp = (index: number) => Math.min(lineLength - 1, Math.max(0, index));

  for (let line = 0; line < lineCount; line += 1) {
    const base = line * lineStep;
    const sums = [0, 0, 0, 0];
    for (let offset = -radius; offset <= radius; offset += 1) {
      const index = base + clamp(offset) * step;
      for (let channel = 0; channel < 4; channel += 1) sums[channel] += source[index + channel];
    }
    for (let position = 0; position < lineLength; position += 1) {
      const out = base + position * step;
      const leaving = base + clamp(position - radius) * step;
      const entering = base + clamp(position + radius + 1) * step;
      for (let channel = 0; channel < 4; channel += 1) {
        target[out + channel] = sums[channel] / window;
        sums[channel] += source[entering + channel] - source[leaving + channel];
      }
    }
  }
}

/** Three box passes approximate a gaussian at O(1) per pixel. */
function applyBlur(
  source: ImageData,
  target: Uint8ClampedArray,
  mask: Mask,
  bounds: Bounds,
  blockSize: number,
): void {
  const { width, height, data } = source;
  const radius = blurRadiusForBlockSize(blockSize);
  // Pad so the kernel reads real neighbours instead of clamping at the crop.
  const pad = radius * BLUR_PASSES;
  const left = Math.max(0, bounds.left - pad);
  const top = Math.max(0, bounds.top - pad);
  const rectWidth = Math.min(width, bounds.right + pad) - left;
  const rectHeight = Math.min(height, bounds.bottom + pad) - top;

  const pixels = new Uint8ClampedArray(rectWidth * rectHeight * 4);
  for (let row = 0; row < rectHeight; row += 1) {
    const start = ((top + row) * width + left) * 4;
    pixels.set(data.subarray(start, start + rectWidth * 4), row * rectWidth * 4);
  }
  const scratch = new Uint8ClampedArray(pixels.length);
  for (let pass = 0; pass < BLUR_PASSES; pass += 1) {
    blurAxis(pixels, scratch, rectWidth, rectHeight, radius, true);
    blurAxis(scratch, pixels, rectWidth, rectHeight, radius, false);
  }

  for (let y = bounds.top; y < bounds.bottom; y += 1) {
    for (let x = bounds.left; x < bounds.right; x += 1) {
      if (!isCovered(mask, x, y)) continue;
      const local = ((y - top) * rectWidth + (x - left)) * 4;
      writePixel(
        target,
        (y * width + x) * 4,
        pixels[local],
        pixels[local + 1],
        pixels[local + 2],
        pixels[local + 3],
      );
    }
  }
}

export function parseHexColor(value: string): [number, number, number] {
  const hex = value.replace("#", "");
  const full = hex.length === 3 ? [...hex].map((char) => char + char).join("") : hex;
  const parsed = Number.parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(parsed)) return [0, 0, 0];
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
}

function applyFill(
  source: ImageData,
  target: Uint8ClampedArray,
  mask: Mask,
  bounds: Bounds,
  color: string,
): void {
  const [red, green, blue] = parseHexColor(color);
  for (let y = bounds.top; y < bounds.bottom; y += 1) {
    for (let x = bounds.left; x < bounds.right; x += 1) {
      if (!isCovered(mask, x, y)) continue;
      writePixel(target, (y * source.width + x) * 4, red, green, blue, 255);
    }
  }
}

/**
 * Renders `effect` from the untouched `source` and writes it into `target`
 * wherever `mask` is selected. Always reading the source means overlapping
 * ranges never blur or pixelate an earlier range's output a second time.
 */
export function applyEffect(
  source: ImageData,
  target: Uint8ClampedArray,
  mask: Mask,
  effect: RegionEffect,
): void {
  const bounds = maskBounds(mask);
  if (!bounds) return;
  if (effect.maskStyle === "fill") {
    applyFill(source, target, mask, bounds, effect.fillColor);
  } else if (effect.maskStyle === "blur") {
    applyBlur(source, target, mask, bounds, effect.blockSize);
  } else {
    applyMosaic(source, target, mask, bounds, Math.max(2, Math.round(effect.blockSize)));
  }
}
